import { combineReducers, Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { createBrowserHistory } from "history";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";

// app
import appState from "./modules/app.state";
import appChat from "./modules/app.chat";
import appUser from "./modules/app.user";
import appEmotes from "./modules/app.emotes";
import appDicerolls from "./modules/app.dicerolls";

// entities
import rooms from "./modules/entities.rooms";
import roomCharacters from "./modules/entities.room.characters";
import roomEffects from "./modules/entities.room.effects";
import roomDecks from "./modules/entities.room.decks";
import roomDices from "./modules/entities.room.dices";
import roomItems from "./modules/entities.room.items";
import roomMembers from "./modules/entities.room.members";
import roomMessages from "./modules/entities.room.messages";
import roomNotes from "./modules/entities.room.notes";
import roomSavedatas from "./modules/entities.room.savedatas";
import roomScenes from "./modules/entities.room.scenes";
import roomMessageTabOrders from "./modules/entities.room.messageTabOrders";

import userFiles from "./modules/entities.user.files";
import userMedia from "./modules/entities.user.media";
import userMediumDirectories from "./modules/entities.user.mediumDirectories";
import userHistories from "./modules/entities.user.histories";
import userSetting from "./modules/entities.user.setting";
import userSoundMastrSettings from "./modules/entities.user.soundMasterSettings";
import turboRooms from "./modules/entities.turboRooms";
import { useDispatch, useSelector } from "react-redux";

// Slices (2.0)
import roomSlice from "containers/Room/roomSlice";
import historySlice from "./modules/entities.room.histories/slice";
import transformSlice from "./modules/entities.room.transform/slice";
import { configureStore, createListenerMiddleware } from "@reduxjs/toolkit";

const appStatePersistConfig = {
  key: "store.app.state",
  storage,
  whitelist: [
    "muted",
    "roomMediaVolume",
    "roomScreenScale",
    "roomChatName",
    "roomChatId",
    "roomChatWideMode",
    "roomChatColor",
    "informationDate",
    "informationVersion",
    "storeVersion",
    "showRoomSystemMessages",
    "userMediumListLibraryTab",
    "registered",
    "officialPlaylistVolume",
    "officialPlaylistRepeat",
    "videoPlayerVolume",
  ],
};

export const history = createBrowserHistory();
export const listener = createListenerMiddleware();

const reducerMap = {
  app: combineReducers({
    state: persistReducer(appStatePersistConfig, appState),
    chat: appChat,
    user: appUser,
    emotes: appEmotes,
    dicerolls: appDicerolls,
    // slices
    room: roomSlice,
    // logs: appLogs
  }),
  entities: combineReducers({
    rooms,
    roomCharacters,
    roomEffects,
    roomDices,
    roomDecks,
    roomItems,
    roomMembers,
    roomMessages,
    roomNotes,
    roomSavedatas,
    roomScenes,
    roomMessageTabOrders,
    userFiles,
    userMedia,
    userMediumDirectories,
    userHistories,
    userSetting,
    userSoundMastrSettings,
    roomHistories: historySlice,
    turboRooms,
  }),
  transforms: combineReducers({
    roomTransform: transformSlice,
  }),
};
const rootReducer = combineReducers(reducerMap);

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: false,
    });
  },
});

export const persistor = persistStore(store);

export type DefaultRootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export type DefaultThunk<R = void> = ThunkAction<
  R,
  DefaultRootState,
  any,
  Action
>;

export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<DefaultRootState>();
